import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useImperativeHandle, useRef } from 'react';
import { useTransitionController } from 'util/react-transition-component';
import useSetupTimeline from '../../../../hooks/useSetupTimeline';
import Heading from '../../../atoms/heading/Heading';
import type {
  ServicePowerSlideBackgroundProps,
  ServicePowerSlideBackgroundRef,
} from './ServicePowerSlideBackground.data';
import styles from './ServicePowerSlideBackground.module.scss';
import {
  scrollInTimeline,
  scrollInTimelineSubtitle,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './ServicePowerSlideBackground.transitions';
import { reMap } from '../../../../util/math.utils';

export default function ServicePowerSlideBackground({
  blok,
  ref,
  ...props
}: ServicePowerSlideBackgroundProps & { ref?: Ref<ServicePowerSlideBackgroundRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<ServicePowerSlideBackgroundRef | null>).current
    ?.elementRef || localRef) as RefObject<HTMLDivElement | null>;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleHolderRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLHeadingElement>(null);
  const subtitleHolderRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const hasTransitionedIn = useRef<boolean>(false);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        titleRef,
        subtitleRef,
        backgroundRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const scrollTimeline = useRef(useSetupTimeline(scrollInTimeline, { titleHolderRef }));
  const scrollTimelineSubtitle = useRef(
    useSetupTimeline(scrollInTimelineSubtitle, { subtitleHolderRef }),
  );

  const setProgress = useCallback(
    (progress: number, slideIndex: number) => {
      if (slideIndex === props.slideIndex) {
        scrollTimeline.current.progress(reMap(progress, 0, 100, 0, 110));
        scrollTimelineSubtitle.current.progress(reMap(progress, 0, 50, 0, 100));
      }

      if (progress > 0 && progress < 1 && !hasTransitionedIn.current) {
        hasTransitionedIn.current = true;
        transitionController.transitionIn();
      } else if ((progress <= 0 || progress >= 1) && hasTransitionedIn.current) {
        hasTransitionedIn.current = false;
        transitionController.transitionOut();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.slideIndex],
  );

  useImperativeHandle(
    ref,
    () => ({
      setProgress,
      elementRef,
    }),
    [setProgress, elementRef],
  );

  return (
    <div
      ref={elementRef}
      style={
        {
          '--foreground-color': props.config.foregroundColor,
        } as React.CSSProperties
      }
      className={classNames(styles.servicePowerSlideBackground, props.className, 'grid-gutter')}
      {...sbEditable(blok)}
    >
      <div
        style={
          {
            '--background-color': props.config.backgroundColor,
          } as React.CSSProperties
        }
        ref={backgroundRef}
        className={classNames(styles.background, 'abs-fill')}
      />
      <div ref={titleHolderRef} className={classNames(styles.titleContent)}>
        <Heading
          ref={titleRef}
          as="h2"
          size="heading-01"
          className={classNames(styles.title)}
          disableTriggerInOnScroll
          transitionOutType="splitCharsSlideOutUp"
          transitionOnMount
        >
          {blok.background_title?.split(' ').map((str) => (
            <span key={str} className={classNames(styles.line, 'text-outline')}>
              {str}
            </span>
          ))}
        </Heading>
      </div>

      <div ref={subtitleHolderRef} className={classNames(styles.textContent)}>
        {blok.background_subtitle && (
          <Heading
            ref={subtitleRef}
            transitionInType="splitWordsSlideInUp"
            transitionOutType="fadeOut"
            as="h4"
            size="heading-05"
            className={classNames(styles.title)}
          >
            {blok.background_subtitle}
          </Heading>
        )}
      </div>
    </div>
  );
}
